import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface UserAdminItem {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
  status: string
  bu: string | null
  managerUuid: string | null
  managerEmail: string | null
  agencyUuid: string | null
  agencyName: string | null
  permissions: string[]
  dateCreated: string
  dateUpdated: string
}

export interface AgencyGroupOption {
  label: string
  options: { id: string; value: string; label: string }[]
}

interface UserAdminAccessToken {
  uuid: string
  tokenType: string
  issuedAt: string
  expiresAt: string | null
  revokedAt: string | null
  revokedReason: string | null
}

export interface UserAdminManagedItem {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
  status: string
  bu: string | null
}

export interface UserPasswordInfo {
  hasPassword: boolean
  passwordSetAt: string | null
  mustRotate: boolean
  failedAttempts: number
  lastFailedAt: string | null
  lastSuccessAt: string | null
  lockedUntil: string | null
}

export interface UserResetToken {
  uuid: string
  requestedAt: string
  expiresAt: string
  usedAt: string | null
  revokedAt: string | null
  requestReason: string | null
  requestIp: string | null
}

export interface BrevoEmail {
  uuid: string
  date: string
  subject: string
  messageId: string | null
  event: string | null
  email: string
}

export interface UserAdminDetail extends UserAdminItem {
  accessTokens: UserAdminAccessToken[]
  roleGroups?: RoleGroupItem[]
  managedUsers?: UserAdminManagedItem[]
  metadata?: Record<string, unknown> | null
  preferences?: PreferenceItem
  m365Oid?: string | null
}

export interface PreferenceItem {
  receiveDirectEmails: boolean
  receiveManagerEmails: boolean
}

interface LuccaManagerProposal {
  status: 'no_manager_on_lucca' | 'manager_not_in_boostcore' | 'already_correct' | 'change_proposed'
  luccaManagerEmail: string | null
  luccaManagerName: string | null
  currentManagerUuid: string | null
  proposedManagerUuid: string | null
  proposedManagerEmail: string | null
  proposedManagerName: string | null
}

interface LuccaManagedProposal {
  status: 'no_managed_on_lucca' | 'all_correct' | 'changes_proposed'
  toAdd: Array<{ uuid: string; email: string; name: string; luccaId: number }>
  toRemove: Array<{ uuid: string; email: string; name: string }>
  notInBoostcore: Array<{ luccaId: number; email: string; name: string }>
}

export interface LuccaSyncResult {
  foundOnLucca: boolean
  stillEmployed: boolean
  contractEndDate: string | null
  luccaUser: {
    id: number
    mail: string | null
    firstName: string | null
    lastName: string | null
    employeeNumber: string | null
    dtContractStart: string | null
    dtContractEnd: string | null
  } | null
  managerProposal: LuccaManagerProposal | null
  managedProposal: LuccaManagedProposal | null
}

export interface OdooSyncResult {
  foundOnOdoo: boolean
  odooUser: {
    id: number
    name: string | null
    email: string | null
    partnerId: number | null
  } | null
}

export interface LuccaLookupResult {
  foundOnLucca: boolean
  existingUser: {
    uuid: string
    email: string
    firstName: string | null
    lastName: string | null
    status: string
  } | null
  luccaId: number | null
  employeeNumber: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
}

export interface PermissionItem {
  code: string
  description: string | null
}

export interface RoleGroupItem {
  uuid: string
  name: string
  description: string | null
  permissions: { code: string; description: string | null }[]
}

export const useUserAdminStore = defineStore('userAdmin', () => {
  const suiteStore = useSuiteStore()
  const users = ref<UserAdminItem[]>([])
  const total = ref(0)
  const limit = ref(50)
  const offset = ref(0)
  const permissions = ref<PermissionItem[]>([])
  const roleGroups = ref<RoleGroupItem[]>([])
  const agenciesGrouped = ref<AgencyGroupOption[]>([])
  const bus = ref<{ id: string; value: string; label: string }[]>([])
  const usersForManagers = ref<UserAdminItem[]>([])
  const isLoading = ref(false)
  const currentUser = ref<UserAdminDetail | null>(null)

  async function fetchUsersForManagers(): Promise<UserAdminItem[]> {
    const res = await suiteStore.suiteFetch('users/admin/list?limit=500', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      usersForManagers.value = json.data ?? []
      return usersForManagers.value
    }
    return []
  }

  async function fetchUsers(params?: {
    limit?: number
    offset?: number
    orderBy?: string
    order?: 'asc' | 'desc'
    search?: string
    bu?: string | null
    permission?: string | null
    roleGroupUuid?: string | null
    status?: 'ACTIVE' | 'DISABLED' | 'SUSPENDED' | null
  }): Promise<{ data: UserAdminItem[]; total: number }> {
    isLoading.value = true
    try {
      const q = new URLSearchParams()
      if (params?.limit) q.set('limit', String(params.limit))
      if (params?.offset) q.set('offset', String(params.offset))
      if (params?.orderBy) q.set('orderBy', params.orderBy)
      if (params?.order) q.set('order', params.order)
      if (params?.search) q.set('search', params.search)
      if (params?.bu) q.set('bu', params.bu)
      if (params?.permission) q.set('permission', params.permission)
      if (params?.roleGroupUuid) q.set('roleGroupUuid', params.roleGroupUuid)
      if (params?.status) q.set('status', params.status)
      const res = await suiteStore.suiteFetch(`users/admin/list?${q}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        users.value = json.data ?? []
        total.value = json.total ?? 0
        limit.value = json.limit ?? 50
        offset.value = json.offset ?? 0
        return { data: users.value, total: total.value }
      }
    } catch (e) {
      console.error('Erreur récupération utilisateurs admin', e)
    } finally {
      isLoading.value = false
    }
    return { data: [], total: 0 }
  }

  async function fetchBus(): Promise<{ id: string; value: string; label: string }[]> {
    const res = await suiteStore.suiteFetch('users/admin/bus', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = await res.json()
      bus.value = data ?? []
      return bus.value
    }
    return []
  }

  async function fetchPermissions(): Promise<PermissionItem[]> {
    const res = await suiteStore.suiteFetch('users/admin/permissions', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      permissions.value = json ?? []
      return permissions.value
    }
    return []
  }

  async function fetchRoleGroups(): Promise<RoleGroupItem[]> {
    const res = await suiteStore.suiteFetch('users/admin/role-groups', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      roleGroups.value = json ?? []
      return roleGroups.value
    }
    return []
  }

  async function fetchAgenciesGrouped(): Promise<AgencyGroupOption[]> {
    const res = await suiteStore.suiteFetch('users/admin/agencies', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      agenciesGrouped.value = json ?? []
      return agenciesGrouped.value
    }
    return []
  }

  async function addRoleGroup(userUuid: string, roleGroupUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/role-groups`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roleGroupUuid }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchUserDetail(userUuid)
      const u = users.value.find((x) => x.uuid === userUuid)
      if (u) {
        const rg = roleGroups.value.find((g) => g.uuid === roleGroupUuid)
        if (rg) {
          const newPerms = new Set(u.permissions)
          rg.permissions.forEach((p) => newPerms.add(p.code))
          u.permissions = Array.from(newPerms)
        }
      }
      return true
    }
    return false
  }

  async function removeRoleGroup(userUuid: string, roleGroupUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/role-groups/${roleGroupUuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchUserDetail(userUuid)
      const u = users.value.find((x) => x.uuid === userUuid)
      if (u) {
        const rg = roleGroups.value.find((g) => g.uuid === roleGroupUuid)
        if (rg) {
          const toRemove = new Set(rg.permissions.map((p) => p.code))
          u.permissions = u.permissions.filter((p) => !toRemove.has(p))
        }
      }
      return true
    }
    return false
  }

  async function fetchUserDetail(uuid: string): Promise<UserAdminDetail | null> {
    const res = await suiteStore.suiteFetch(`users/admin/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      currentUser.value = json
      return json
    }
    return null
  }

  async function updateUser(
    uuid: string,
    dto: {
      status?: string
      bu?: string | null
      managerUuid?: string | null
      agencyUuid?: string | null
      payplanLevel?: 'JUNIOR' | 'SENIOR' | null
      preferences?: PreferenceItem
    },
  ): Promise<boolean> {
    console.log('dto', dto)
    const res = await suiteStore.suiteFetch(`users/admin/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const ag = dto.agencyUuid !== undefined
        ? agenciesGrouped.value.flatMap((g) => g.options).find((o) => o.value === dto.agencyUuid)
        : null
      const agencyName = ag ? ag.label : (dto.agencyUuid ? null : undefined)
      if (currentUser.value?.uuid === uuid) {
        Object.assign(currentUser.value, dto)
        // Le barème est stocké dans metadata.payplanLevel côté backend : on garde metadata en phase.
        if (dto.payplanLevel !== undefined) {
          currentUser.value.metadata = { ...(currentUser.value.metadata as Record<string, unknown> | null ?? {}), payplanLevel: dto.payplanLevel }
        }
        if (dto.managerUuid !== undefined) {
          const mgr = usersForManagers.value.find((u) => u.uuid === dto.managerUuid)
          currentUser.value.managerEmail = mgr ? mgr.email : null
        }
        if (agencyName !== undefined) currentUser.value.agencyName = agencyName ?? null
      }
      const idx = users.value.findIndex((u) => u.uuid === uuid)
      if (idx >= 0) {
        Object.assign(users.value[idx], dto)
        if (dto.managerUuid !== undefined) {
          const mgr = usersForManagers.value.find((u) => u.uuid === dto.managerUuid)
          users.value[idx].managerEmail = mgr ? mgr.email : null
        }
        if (agencyName !== undefined) users.value[idx].agencyName = agencyName ?? null
      }
      console.log('currentUser', currentUser.value)

      return true
    }
    return false
  }

  async function addPermission(userUuid: string, permissionCode: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/permissions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissionCode }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      if (currentUser.value?.uuid === userUuid && !currentUser.value.permissions.includes(permissionCode)) {
        currentUser.value.permissions.push(permissionCode)
      }
      const u = users.value.find((x) => x.uuid === userUuid)
      if (u && !u.permissions.includes(permissionCode)) u.permissions.push(permissionCode)
      return true
    }
    return false
  }

  async function removePermission(userUuid: string, permissionCode: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/permissions/${encodeURIComponent(permissionCode)}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      if (currentUser.value?.uuid === userUuid) {
        currentUser.value.permissions = currentUser.value.permissions.filter((p) => p !== permissionCode)
      }
      const u = users.value.find((x) => x.uuid === userUuid)
      if (u) u.permissions = u.permissions.filter((p) => p !== permissionCode)
      return true
    }
    return false
  }

  async function revokeAccessToken(userUuid: string, tokenUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/access-tokens/${tokenUuid}/revoke`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      if (currentUser.value?.uuid === userUuid) {
        const t = currentUser.value.accessTokens.find((x) => x.uuid === tokenUuid)
        if (t) {
          t.revokedAt = new Date().toISOString()
          t.revokedReason = 'ADMIN_REVOKE'
        }
      }
      return true
    }
    return false
  }

  async function revokeAllAccessTokens(userUuid: string): Promise<{ ok: boolean; count: number }> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/access-tokens/revoke-all`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json().catch(() => ({ count: 0 }))) as { count?: number }
      const nowIso = new Date().toISOString()
      if (currentUser.value?.uuid === userUuid) {
        for (const t of currentUser.value.accessTokens) {
          if (!t.revokedAt) {
            t.revokedAt = nowIso
            t.revokedReason = 'ADMIN_REVOKE'
          }
        }
      }
      return { ok: true, count: data.count ?? 0 }
    }
    return { ok: false, count: 0 }
  }

  async function fetchBrevoEmails(email: string, params?: { limit?: number; offset?: number }): Promise<{ emails: BrevoEmail[]; count: number }> {
    const q = new URLSearchParams()
    q.set('email', email)
    if (params?.limit != null) q.set('limit', String(params.limit))
    if (params?.offset != null) q.set('offset', String(params.offset))
    const res = await suiteStore.suiteFetch(`brevo/emails?${q}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) return res.json()
    return { emails: [], count: 0 }
  }

  async function fetchPasswordInfo(userUuid: string): Promise<UserPasswordInfo> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/password-info`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) return res.json()
    return { hasPassword: false, passwordSetAt: null, mustRotate: false, failedAttempts: 0, lastFailedAt: null, lastSuccessAt: null, lockedUntil: null }
  }

  async function fetchResetTokens(userUuid: string): Promise<UserResetToken[]> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/reset-tokens`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) return res.json()
    return []
  }

  async function sendResetEmail(userUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`auth/user/admin/send-reset/${userUuid}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  async function lookupLuccaUser(email: string): Promise<LuccaLookupResult | null> {
    const q = new URLSearchParams({ email })
    const res = await suiteStore.suiteFetch(`users/admin/lucca/lookup?${q}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) return res.json()
    return null
  }

  async function createUser(dto: { email: string; firstName?: string | null; lastName?: string | null }): Promise<
    { ok: true; user: { uuid: string; email: string; firstName: string | null; lastName: string | null; foundOnLucca: boolean } }
    | { ok: false; existingUserUuid?: string; message?: string }
  > {
    const res = await suiteStore.suiteFetch('users/admin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const user = await res.json()
      return { ok: true, user }
    }
    if (res && typeof res !== 'boolean') {
      try {
        const body = await res.json()
        const msgField = body?.message
        const message =
          typeof msgField === 'string'
            ? msgField
            : (msgField?.message ?? body?.error ?? 'Erreur lors de la création')
        if (res.status === 409) {
          const existingUserUuid = body?.existingUserUuid ?? msgField?.existingUserUuid
          return { ok: false, existingUserUuid, message }
        }
        return { ok: false, message }
      } catch {
        return { ok: false, message: 'Erreur lors de la création' }
      }
    }
    return { ok: false, message: 'Erreur lors de la création' }
  }

  async function syncUserFromLucca(userUuid: string): Promise<LuccaSyncResult | null> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/sync-lucca`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      if (currentUser.value?.uuid === userUuid && json.luccaUser) {
        const meta = (currentUser.value.metadata as Record<string, unknown>) ?? {}
        currentUser.value.metadata = {
          ...meta,
          luccaId: json.luccaUser.id,
          luccaEmployeeNumber: json.luccaUser.employeeNumber,
        }
      }
      return json
    }
    return null
  }

  async function applyLuccaManager(
    userUuid: string,
    proposedManagerUuid: string,
  ): Promise<{ ok: boolean; managerUuid: string; managerEmail: string; managerName: string } | null> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/apply-lucca-manager`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proposedManagerUuid }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      if (currentUser.value?.uuid === userUuid) {
        currentUser.value.managerUuid = json.managerUuid
      }
      return json
    }
    return null
  }

  async function applyLuccaManaged(
    userUuid: string,
    uuidsToAdd: string[],
  ): Promise<{ ok: boolean; applied: number } | null> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/apply-lucca-managed`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uuidsToAdd }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return await res.json()
    }
    return null
  }

  async function syncUserFromOdoo(userUuid: string): Promise<OdooSyncResult | null> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/sync-odoo`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = (await res.json()) as OdooSyncResult
      if (currentUser.value?.uuid === userUuid && json.odooUser) {
        const meta = (currentUser.value.metadata as Record<string, unknown>) ?? {}
        currentUser.value.metadata = {
          ...meta,
          odoo16Id: json.odooUser.id,
          odoo16PartnerId: json.odooUser.partnerId,
        }
      }
      return json
    }
    return null
  }

  async function generateTricksterToken(userUuid: string): Promise<{
    ok: boolean
    csrfToken: string
    expiresAt: string
    targetUser: { uuid: string; email: string; firstName: string | null; lastName: string | null }
  } | null> {
    const res = await suiteStore.suiteFetch(`users/admin/${userUuid}/trickster`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (res && typeof res !== 'boolean' && res.ok) return res.json()
    return null
  }

  function getUserLabel(u: UserAdminItem | UserAdminManagedItem): string {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ')
    return name || u.email
  }

  return {
    users,
    total,
    limit,
    offset,
    permissions,
    roleGroups,
    agenciesGrouped,
    bus,
    usersForManagers,
    currentUser,
    isLoading,
    fetchUsers,
    fetchUsersForManagers,
    fetchPermissions,
    fetchRoleGroups,
    fetchAgenciesGrouped,
    fetchBus,
    fetchUserDetail,
    updateUser,
    addPermission,
    removePermission,
    addRoleGroup,
    removeRoleGroup,
    revokeAccessToken,
    revokeAllAccessTokens,
    fetchBrevoEmails,
    fetchPasswordInfo,
    fetchResetTokens,
    sendResetEmail,
    generateTricksterToken,
    lookupLuccaUser,
    createUser,
    syncUserFromLucca,
    applyLuccaManager,
    applyLuccaManaged,
    syncUserFromOdoo,
    getUserLabel,
  }
})
